import { useEffect, useState, type JSX, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ExternalLink, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { PlatformIcon } from '@/components/PlatformIcon';
import { AccountTypeBadge, FoundViaBadge } from '../AccountTypeBadge';
import { AudienceBadge, flag } from '../AudienceBadge';
import { PresenterBadge } from '../PresenterBadge';
import { OutreachStatusBadge, doneReasonLabel } from '../OutreachStatus';
import { engagement, formatEngagement } from '../../lib/engagement';
import { PLATFORM_LABELS, profileUrl } from '../../lib/platform';
import { useDiscoveryPaths } from '../../workspace/paths';
import { assigneeLabel, formatCount, formatDate, type LeadRow } from './rows';

export interface SheetAction {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
  primary?: boolean;
}

/**
 * Everything about one creator, opened from a lead-table row: profile stats,
 * audience, how they were found, other folders, where they are in outreach
 * (assignee, claimed by), notes, and every action as a labelled button.
 */
export function CreatorSheet({
  row,
  open,
  onOpenChange,
  onPrev,
  onNext,
  actions,
  onSaveNotes,
  savingNotes,
}: {
  row: LeadRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPrev?: () => void;
  onNext?: () => void;
  actions: readonly SheetAction[];
  /** Folder leads have editable notes; registry creators show theirs read-only. */
  onSaveNotes?: (notes: string) => void;
  savingNotes?: boolean;
}): JSX.Element {
  return (
    <Sheet open={open && !!row} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-xl">
        {row && (
          <SheetBody
            row={row}
            onPrev={onPrev}
            onNext={onNext}
            actions={actions}
            onSaveNotes={onSaveNotes}
            savingNotes={savingNotes}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function SheetBody({
  row,
  onPrev,
  onNext,
  actions,
  onSaveNotes,
  savingNotes,
}: {
  row: LeadRow;
  onPrev?: () => void;
  onNext?: () => void;
  actions: readonly SheetAction[];
  onSaveNotes?: (notes: string) => void;
  savingNotes?: boolean;
}): JSX.Element {
  const paths = useDiscoveryPaths();
  const e = row.enrichment ?? {};
  const r = row.record;
  const eng = engagement(row.enrichment);
  const [notes, setNotes] = useState(row.notes ?? '');
  useEffect(() => setNotes(row.notes ?? ''), [row.key, row.notes]);

  const audience = r.audienceGeo && r.audienceGeo.sample > 0 ? r.audienceGeo : null;
  const topCountries = audience
    ? Object.entries(audience.countries)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
    : [];
  const summary = [
    e.nickname ? String(e.nickname) : null,
    `${formatCount(e.followers)} followers`,
    PLATFORM_LABELS[row.platform],
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="space-y-3 border-b border-border px-5 pb-4 pt-5">
        <div className="flex items-start gap-3 pr-8">
          <PlatformIcon platform={row.platform} size={18} className="mt-1 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <SheetTitle className="flex flex-wrap items-center gap-2 text-lg font-semibold">
              <a href={profileUrl(row.platform, row.username)} target="_blank" rel="noopener noreferrer" className="hover:underline">
                @{row.username}
              </a>
              <OutreachStatusBadge creator={r} />
            </SheetTitle>
            <SheetDescription className="mt-0.5 truncate text-sm text-muted-foreground">{summary}</SheetDescription>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {actions.map((a) => (
            <Button
              key={a.label}
              size="sm"
              variant={a.primary ? 'default' : a.destructive ? 'ghost' : 'outline'}
              className={cn(a.destructive && 'text-destructive-strong')}
              disabled={a.disabled}
              onClick={a.onClick}
            >
              <a.icon className="h-3.5 w-3.5" />
              {a.label}
            </Button>
          ))}
          <Button size="sm" variant="ghost" asChild>
            <a href={profileUrl(row.platform, row.username)} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-3.5 w-3.5" />
              Profile
            </a>
          </Button>
          {(onPrev || onNext) && (
            <span className="ml-auto inline-flex items-center gap-1">
              <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Previous creator" title="Previous creator" disabled={!onPrev} onClick={onPrev}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Next creator" title="Next creator" disabled={!onNext} onClick={onNext}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </span>
          )}
        </div>
      </header>

      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
        <Section title="Profile">
          <Stat label="Followers" value={formatCount(e.followers)} />
          <Stat label="Avg views" value={formatCount(e.avgViews)} />
          <Stat label="Engagement" value={formatEngagement(eng)} />
          <Stat label="Posts" value={formatCount(e.videoCount)} />
          <Stat label="Last post" value={e.lastPostedDaysAgo != null ? `${e.lastPostedDaysAgo}d ago` : '—'} />
          <Stat label="Email" value={e.email ? String(e.email) : '—'} />
          <Stat
            label="Type"
            value={
              <span className="inline-flex flex-wrap items-center justify-end gap-1.5">
                <AccountTypeBadge accountType={r.accountType} confidence={r.accountTypeConfidence} signals={r.accountTypeSignals} />
                <PresenterBadge
                  presenter={r.presenter}
                  confidence={r.presenterConfidence}
                  presentation={r.presenterPresentation}
                  formats={r.presenterFormats}
                  reason={r.presenterReason}
                  profile={r.presenterProfile}
                />
              </span>
            }
          />
          {e.bio && <p className="col-span-2 whitespace-pre-line pt-1 text-sm text-muted-foreground">{String(e.bio)}</p>}
        </Section>

        <Section title="Audience">
          {audience || e.region ? (
            <div className="col-span-2 space-y-2">
              <AudienceBadge region={e.region as string | undefined} audience={r.audienceGeo} />
              {topCountries.length > 0 && audience && (
                <ul className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                  {topCountries.map(([code, n]) => (
                    <li key={code} className="flex justify-between gap-3">
                      <span>
                        {flag(code)} {code}
                      </span>
                      <span className="tabular-nums text-muted-foreground">{Math.round((n / audience.sample) * 100)}%</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <p className="col-span-2 text-sm text-muted-foreground">Not measured yet.</p>
          )}
        </Section>

        <Section title="Outreach">
          <Stat label="Status" value={<OutreachStatusBadge creator={r} />} />
          <Stat label="Assigned to" value={assigneeLabel(row.assignedTo) ?? 'Unassigned'} />
          <Stat label="Claimed by" value={row.claimedByName ?? '—'} />
          <Stat label="Last messaged" value={formatDate(row.lastContactedAt)} />
          {row.kind === 'lead' && <Stat label="Done" value={doneReasonLabel(row.doneReason) ?? 'Open'} />}
        </Section>

        <Section title={row.kind === 'lead' ? 'Found via' : 'Folders'}>
          {row.kind === 'lead' && row.discoveryMethod && (
            <Stat
              label="Search"
              value={<FoundViaBadge method={row.discoveryMethod} source={row.discoverySource} topicMatch={row.topicMatch} />}
            />
          )}
          {row.kind === 'lead' && <Stat label="Added" value={formatDate(row.addedAt)} />}
          <div className="col-span-2">
            <p className="mb-1 text-sm text-muted-foreground">{row.kind === 'lead' ? 'Also in' : 'In'}</p>
            {row.folders.length > 0 ? (
              <ul className="flex flex-wrap gap-1.5">
                {row.folders.map((f) => (
                  <li key={f.publicId}>
                    <Link to={paths.folder(f.publicId)} className="rounded-md border border-border px-2 py-0.5 text-sm hover:bg-accent">
                      {f.name}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : row.folderNames.length > 0 ? (
              <p className="text-sm">{row.folderNames.join(', ')}</p>
            ) : (
              <p className="text-sm text-muted-foreground">No other folders.</p>
            )}
          </div>
        </Section>

        <Section title="Notes">
          {onSaveNotes ? (
            <div className="col-span-2 space-y-2">
              <textarea
                aria-label="Notes"
                className="min-h-[5rem] w-full resize-y rounded-lg border border-input bg-[var(--input-bg)] px-3 py-2 text-sm shadow-control placeholder:text-muted-foreground/70 focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/25"
                placeholder="Anything worth remembering about this creator"
                value={notes}
                maxLength={4000}
                onChange={(ev) => setNotes(ev.target.value)}
              />
              <Button size="sm" variant="outline" disabled={savingNotes || notes === (row.notes ?? '')} onClick={() => onSaveNotes(notes)}>
                {savingNotes ? 'Saving…' : 'Save notes'}
              </Button>
            </div>
          ) : (
            <p className="col-span-2 whitespace-pre-line text-sm text-muted-foreground">{row.notes || 'No notes.'}</p>
          )}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <section>
      <h3 className="mb-2 text-sm font-semibold">{title}</h3>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">{children}</div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: ReactNode }): JSX.Element {
  return (
    <div className="col-span-2 flex items-baseline justify-between gap-4 text-sm sm:col-span-1">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-right tabular-nums">{value}</span>
    </div>
  );
}
